import type { VisibilityState } from '@/lib/tanstack-table';

/** A table column a board filter can narrow, and how to compare its cells. */
export interface FilterableColumn<Row> {
  id: string;
  /** An active filter acts on the column. */
  filtered: boolean;
  /**
   * The filter keeps exactly one value of the column, so rows the table has
   * not loaded (later pages) show that value too.
   */
  singlePick?: boolean;
  /** What makes two rows' cells the same. */
  key: (row: Row) => string;
  /** The words the cell shows, for the "show again" menu. */
  label: (row: Row) => string;
}

export interface ColumnHiddenByFilter {
  id: string;
  /** The one value every row shows, as its cell shows it. */
  value: string;
}

/**
 * The columns an active filter narrowed to exactly one value: a filter acts on
 * the column and every row shows the same value in it, so the column would
 * repeat that value down the table. On one page of a longer result
 * (`complete: false`) only a single-pick filter can vouch for the other pages.
 */
export function columnsHiddenByFilters<Row>(
  columns: readonly FilterableColumn<Row>[],
  rows: readonly Row[],
  { complete = true }: { complete?: boolean } = {},
): ColumnHiddenByFilter[] {
  const [first] = rows;
  if (first === undefined) return [];
  return columns.flatMap(({ id, filtered, singlePick, key, label }) => {
    if (!filtered || !(complete || singlePick)) return [];
    const firstKey = key(first);
    return rows.every((row) => key(row) === firstKey)
      ? [{ id, value: label(first) }]
      : [];
  });
}

/**
 * A table's one column visibility state for its filter-narrowed columns:
 * `false` hides one, `true` marks one the buyer showed again.
 */
export function filterColumnVisibility(
  hiddenByFilters: readonly ColumnHiddenByFilter[],
  shownColumnIds: ReadonlySet<string>,
): VisibilityState {
  return Object.fromEntries(
    hiddenByFilters.map(({ id }) => [id, shownColumnIds.has(id)]),
  );
}
